import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { AdditionalReferenceFormValues } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankAdditionalReference } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'
import FormFieldArray from '../../../../components/p5/FormFieldArray'
import FormField from '../../../../components/p5/FormField'
import FormSelect from '../../../../components/p5/FormSelect'
import Input from '../../../../components/p5/Input'

const scope = 'additionalReference'

function AdditionalReferences() {
  const { t } = useTranslation()

  const { control } = useFormContext<ConsignmentItemFormValues>()
  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<ConsignmentItemFormValues, AdditionalReferenceFormValues>({
    control,
    name: scope,
  })

  const [, additionalReferencesOptions] = useCodelist('ADDITIONAL_REFERENCES')

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((f) => `${scope}.${f.index}.type`),
      ...visibleFields.map((f) => `${scope}.${f.index}.referenceNumber`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<ConsignmentItemFormValues>('consignmentItem', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-additional-references"
        headingLabel={t('declaration.p5.additionalReferences')}
        fieldRule={fieldRequirements[scope]}
      >
        {visibleFields.map((fieldWithIndex) => {
          const additionalReference = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

          return (
            <FormFieldArray
              key={`additionalReference_${additionalReference.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={additionalReference.sequenceNumber + 1}
              hidden={additionalReference.deleted}
            >
              <FormField
                labelKey="declaration.p5.additionalReferenceType"
                fieldRule={fieldRequirements[`${innerItemScope}.type`]}
              >
                <FormSelect
                  <ConsignmentItemFormValues>
                  field={`${innerItemScope}.type`}
                  labelKey="declaration.p5.additionalReferenceType"
                  options={additionalReferencesOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.type`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.additionalReferenceNumber"
                fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
              >
                <Input
                  <ConsignmentItemFormValues>
                  type="text"
                  field={`${innerItemScope}.referenceNumber`}
                  labelKey="declaration.p5.additionalReferenceNumber"
                  fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankAdditionalReference)}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewReference')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default AdditionalReferences
